<template>
<div>
    <div class="mt-2">
        <v-btn text class="grey--text text--darken-1" @click="$router.push('/admin-videos')">
            <v-icon size="14px" color="grey darken-1" class="mr-3">fa-angle-left</v-icon>
            Back to Video Admin List
        </v-btn>
    </div>

    <v-row v-if="loaded">
        <v-col cols="12" sm="8">
            <v-card outlined>
                <v-card-text :class="($vuetify.breakpoint.xsOnly) ? 'pa-4' : 'px-8 py-4'">
                    <div class="subtitle-1 font-weight-medium primary--text mb-4">Create new course</div>

                    <!-- CATEGORY -->
                    <v-select
                        v-model="model.category"
                        :items="categories"
                        item-text="title"
                        item-value="id"
                        append-icon="fa-angle-down accent--text"
                        outlined dense
                        label="Select the category">
                    </v-select>

                    <!-- TITLE -->
                    <v-text-field
                        v-model="model.title"
                        label="Title"
                        maxlength="200"
                        outlined dense
                        @keydown.enter="validate">
                        <template v-slot:prepend>
                            <v-icon color="info" class="mt-1 mr-1" small>fa-tag fa-fw</v-icon>
                        </template>
                    </v-text-field>

                    <!-- SUBTITLE -->
                    <v-text-field
                        v-model="model.subtitle"
                        label="Sub Title"
                        maxlength="250"
                        outlined dense
                        @keydown.enter="validate">
                        <template v-slot:prepend>
                            <v-icon color="info" class="mt-1 mr-1" small>fa-tags fa-fw</v-icon>
                        </template>
                    </v-text-field>

                    <!-- INVITATION -->
                    <v-textarea
                        v-model="model.invitation"
                        label="Invitation"
                        maxlength="600"
                        outlined dense
                        @keydown.enter="validate">
                        <template v-slot:prepend>
                            <v-icon color="info" class="mt-1 mr-1" small>fa-comment-alt fa-fw</v-icon>
                        </template>
                    </v-textarea>

                    <!-- SUMMARY -->
                    <v-textarea
                        v-model="model.summary"
                        label="Summary"
                        maxlength="600"
                        outlined dense
                        @keydown.enter="validate">
                        <template v-slot:prepend>
                            <v-icon color="info" class="mt-1 mr-1" small>fa-comment-alt fa-fw</v-icon>
                        </template>
                    </v-textarea>

                    <!-- OBJECTIVE -->
                    <div class="ml-8 mb-1 subtitle-1">Objectives:</div>
                    <div v-for="(item, index) in model.objectives" :key="item.id">
                        <div class="d-flex align-center">
                            <v-text-field
                                class="pr-2"
                                v-model="model.objectives[index]"
                                :label="(index+1) + '.'"
                                maxlength="400"
                                outlined dense
                                @keydown.enter="validate">
                                <template v-slot:prepend>
                                    <v-icon color="info" class="mt-1 mr-1" small>fa-minus fa-fw</v-icon>
                                </template>
                            </v-text-field>
                            <v-btn 
                                v-if="index == model.objectives.length-1"
                                :disabled="model.objectives[index] == ''"
                                depressed fab small
                                class="mt-n6" 
                                color="primary"
                                @click="model.objectives.push('')">
                                <v-icon small color="white">fa-plus</v-icon>
                            </v-btn>
                        </div>
                    </div>

                    <!-- SUGGESTIONS -->
                    <div class="ml-8 mb-1 subtitle-1">Suggestions:</div>
                    <div v-for="(item, index) in model.suggestions" :key="item.id">
                        <div class="d-flex align-center">
                            <v-text-field
                                class="pr-2"
                                v-model="model.suggestions[index]"
                                :label="(index+1) + '.'"
                                maxlength="400"
                                outlined dense
                                @keydown.enter="validate">
                                <template v-slot:prepend>
                                    <v-icon color="info" class="mt-1 mr-1" small>fa-minus fa-fw</v-icon>
                                </template>
                            </v-text-field>
                            <v-btn 
                                v-if="index == model.suggestions.length-1"
                                :disabled="model.suggestions[index] == ''"
                                depressed fab small
                                class="mt-n6" 
                                color="primary"
                                @click="model.suggestions.push('')">
                                <v-icon small color="white">fa-plus</v-icon>
                            </v-btn>
                        </div>
                    </div>
                    
                    <!-- LESSION TIPS -->
                    <div class="ml-8 mb-1 subtitle-1">Lesson Tips:</div>
                    <div v-for="(item, index) in model.lessonTips" :key="item.id">
                        <div class="d-flex align-center">
                            <v-text-field
                                class="pr-2"
                                v-model="model.lessonTips[index]"
                                :label="(index+1) + '.'"
                                maxlength="400"
                                outlined dense
                                @keydown.enter="validate">
                                <template v-slot:prepend>
                                    <v-icon color="info" class="mt-1 mr-1" small>fa-minus fa-fw</v-icon>
                                </template>
                            </v-text-field>
                            <v-btn 
                                v-if="index == model.lessonTips.length-1"
                                :disabled="model.lessonTips[index] == ''"
                                depressed fab small
                                class="mt-n6" 
                                color="primary"
                                @click="model.lessonTips.push('')">
                                <v-icon small color="white">fa-plus</v-icon>
                            </v-btn>
                        </div>
                    </div>

                    <!-- CONCLUSION -->
                    <v-textarea
                        v-model="model.conclusion"
                        label="Conclusion"
                        maxlength="500"
                        outlined dense
                        @keydown.enter="validate">
                        <template v-slot:prepend>
                            <v-icon color="info" class="mt-1 mr-1" small>fa-comment-alt fa-fw</v-icon>
                        </template>
                    </v-textarea>

                    <!-- CHALLENGE -->
                    <v-textarea
                        v-model="model.challenge"
                        label="Challenge"
                        maxlength="500"
                        outlined dense
                        @keydown.enter="validate">
                        <template v-slot:prepend>
                            <v-icon color="info" class="mt-1 mr-1" small>fa-comment-alt fa-fw</v-icon>
                        </template>
                    </v-textarea> 

                    <!-- VIDEOS -->
                    <div class="ml-8 mb-1 subtitle-1">Videos:</div>
                    <div v-for="(item, index) in model.videos" :key="item.idx">
                        <div class="mb-2">
                            <v-text-field
                                class="pr-2"
                                v-model="model.videos[index].title"
                                label="Title"
                                maxlength="100"
                                outlined dense
                                @keydown.enter="validate">
                                <template v-slot:prepend>
                                    <v-icon color="info" class="mt-1 mr-1" small>fa-video fa-fw</v-icon>
                                </template>
                            </v-text-field>
                            <v-text-field
                                class="pr-2 mt-n4"
                                v-model="model.videos[index].id"
                                label="Vimeo ID"
                                maxlength="100"
                                outlined dense
                                @keydown.enter="validate">
                                <template v-slot:prepend>
                                    <v-icon color="info" class="mt-1 mr-1" small>fa-minus fa-fw</v-icon>
                                </template>
                            </v-text-field>
                            <div class="mt-n4 pl-8 pr-2" >
                                <v-btn 
                                    v-if="index == model.videos.length-1"
                                    :disabled="model.videos[index].title == ''"
                                    depressed block
                                    color="primary"
                                    @click="model.videos.push({ title: '', id: '' })">
                                    <v-icon small color="white" left>fa-plus</v-icon>
                                    Add one more video
                                </v-btn>
                            </div>
                        </div>
                    </div>  

                    <!-- BUTTON -->
                    <div class="text-end mt-4">
                        <v-btn 
                            depressed 
                            class="accent" 
                            @click="validate" 
                            :disabled="sending">Add course</v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>

    <SendingLoader v-else/>
</div>
</template>

<script>
import _ from 'lodash'
import Helpers from '@/services/helpers'
import Videos from '@/firebase/videos'

export default {
    data: () => ({
        loaded: false,
        sending: false,
        model: {
            category: '',
            title: '',
            subtitle: '',
            invitation: '',
            summary: '',
            objectives: [''],
            suggestions: [''],
            lessonTips: [''],
            conclusion: '',
            challenge: '',
            videos: [{ title: '', id: '' }]
        },
        categories: [],
        conclusion: [],
        challenge: []
    }),
    mounted() {
        this.init();
    },
    methods: {
        init() {
            Videos.getCoursesCategories()
                    .then(response => {
                        if (response.status === true) {
                            this.categories = Helpers.formatItems(response.query);
                            this.loaded = true;
                        } else {
                            this.$store.dispatch('setSnack', {kind:'error', text:'An error ocurred. Please try later.'});
                        }
                    });
        },
        validate() {
            if (this.model.category == '') {
                this.$store.dispatch('setSnack', {kind:'error', text:'You have to select a category.'});
                return;
            }

            if (this.validateIsEmpty(this.model.title)) {
                this.$store.dispatch('setSnack', {kind:'error', text:'You have to set a title.'});
                return;
            }

            if (this.validateIsEmpty(this.model.subtitle)) {
                this.$store.dispatch('setSnack', {kind:'error', text:'You have to set a subtitle.'});
                return;
            }

            if (this.validateIsEmpty(this.model.invitation)) {
                this.$store.dispatch('setSnack', {kind:'error', text:'You have to set an invitation.'});
                return;
            }

            if (this.validateIsEmpty(this.model.summary)) {
                this.$store.dispatch('setSnack', {kind:'error', text:'You have to set a sumary.'});
                return;
            }

            if (this.model.objectives.length == 1 && this.validateIsEmpty(this.model.objectives[0])) {
                this.$store.dispatch('setSnack', {kind:'error', text:'You have to set at least one objective.'});
                return;
            }

            if (this.model.suggestions.length == 1 && this.validateIsEmpty(this.model.suggestions[0])) {
                this.$store.dispatch('setSnack', {kind:'error', text:'You have to set at least one suggestion.'});
                return;
            }

            if (this.model.lessonTips.length == 1 && this.validateIsEmpty(this.model.lessonTips[0])) {
                this.$store.dispatch('setSnack', {kind:'error', text:'You have to set at least one lesson tip.'});
                return;
            }

            if (this.validateIsEmpty(this.model.conclusion)) {
                this.$store.dispatch('setSnack', {kind:'error', text:'You have to set a conclusion.'});
                return;
            }

            if (this.validateIsEmpty(this.model.challenge)) {
                this.$store.dispatch('setSnack', {kind:'error', text:'You have to set a challenge.'});
                return;
            }

            this.prepare();
        },
        prepare() {
            let final = JSON.parse(JSON.stringify(this.model));

            if ( this.validateIsEmpty(final.objectives[final.objectives.length-1]) ) {
                final.objectives.pop();
            }

            if ( this.validateIsEmpty(final.suggestions[final.suggestions.length-1]) ) {
                final.suggestions.pop();
            }

            if ( this.validateIsEmpty(final.lessonTips[final.lessonTips.length-1]) ) {
                final.lessonTips.pop();
            }

            if ( this.validateIsEmpty(final.videos[final.videos.length-1].title) ) {
                final.videos.pop();
            }

            this.add(final);
        },
        add(final) {
            this.sending = true;

            Videos.add(final)
                    .then(response => {
                        if (response === true) {
                            this.$router.push('/admin-videos');
                            this.$store.dispatch('setSnack', {kind:'success', text:'A new course has added.'});
                        } else {
                            this.$store.dispatch('setSnack', {kind:'error', text:'An error ocurred. Please try later.'});
                        }
                    });
        }
    }
}
</script>